import jsPDF from 'jspdf';
import { invoiceRecipient } from './invoiceRecipient';

// ─── Constants ───────────────────────────────────────────────────────────────

const SERENO = {
  name: 'Sereno Tech S.L.',
  cif: 'B44916658',
  address: 'Calle Tulipán, 1',
  city: 'Barbate',
  cp: '11160',
  province: 'Cádiz',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtN(n: number): string {
  return new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
}

function fmt(n: number): string {
  return fmtN(n) + ' €';
}

function formatDateES(dateStr: string): string {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// ─── PDF Generator ───────────────────────────────────────────────────────────

export function generateGuestInvoicePDF(invoice: any): void {
  const doc = new jsPDF({ format: 'a4', unit: 'mm' });
  const W = 210, M = 18, col2 = 115;
  let y = 18;

  const dark: [number, number, number] = [15, 23, 42];
  const gray: [number, number, number] = [100, 116, 139];
  const border: [number, number, number] = [226, 232, 240];
  const accent: [number, number, number] = [30, 41, 59];

  // ─── Title + Invoice number ────────────────────────────────────────
  doc.setFontSize(26);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...dark);
  doc.text('Factura', M, y + 6);

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...gray);
  doc.text(invoice.invoiceNumber, W - M, y + 6, { align: 'right' });

  doc.setDrawColor(...border);
  doc.setLineWidth(0.5);
  doc.line(M, y + 12, W - M, y + 12);
  y += 18;

  // ─── Date ──────────────────────────────────────────────────────────
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...gray);
  doc.text(`Fecha: ${formatDateES(invoice.invoiceDate)}`, M, y);
  y += 8;

  // ─── Emisor + Cliente headers ──────────────────────────────────────
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...gray);
  doc.text('EMISOR', M, y);
  doc.text('CLIENTE', col2, y);
  y += 5;

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...dark);
  doc.setFontSize(9);

  // Use dynamic emisor for gestión invoices, fallback to Sereno
  const emisor = invoice.emisorName
    ? { name: invoice.emisorName, cif: invoice.emisorCif || '', address: invoice.emisorAddress || '' }
    : null;

  const emisorLines = emisor
    ? [
        emisor.name,
        emisor.cif ? `NIF: ${emisor.cif}` : '',
        emisor.address,
      ].filter(Boolean)
    : [
        SERENO.name,
        `CIF: ${SERENO.cif}`,
        SERENO.address,
        `${SERENO.cp} ${SERENO.city} (${SERENO.province})`,
      ];

  // El cliente es el huésped salvo que se hayan indicado datos de facturación
  // propios, que es justo lo que la factura tiene que reflejar
  const cliente = invoiceRecipient(invoice);

  const clienteLines = cliente.isCustom
    ? [
        cliente.name,
        cliente.document ? `${cliente.documentType}: ${cliente.document}` : '',
        cliente.address,
        cliente.email || '',
      ].filter(Boolean)
    : [
        cliente.name,
        cliente.document ? `${invoice.guestDocumentType || 'Doc'}: ${cliente.document}` : '',
        cliente.address,
        invoice.guestNationality ? `Nacionalidad: ${invoice.guestNationality}` : '',
      ].filter(Boolean);

  emisorLines.forEach((l, i) => doc.text(l, M, y + i * 4.5));
  clienteLines.forEach((l, i) => doc.text(l, col2, y + i * 4.5));
  y += Math.max(emisorLines.length, clienteLines.length) * 4.5 + 8;

  // ─── Stay details ─────────────────────────────────────────────────
  doc.setFillColor(245, 247, 250);
  doc.roundedRect(M, y, W - M * 2, 22, 2, 2, 'F');

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...gray);

  const stayY = y + 5;
  const cols = [M + 4, M + 50, M + 96, M + 134];
  doc.text('PROPIEDAD', cols[0], stayY);
  doc.text('CHECK-IN', cols[1], stayY);
  doc.text('CHECK-OUT', cols[2], stayY);
  doc.text('NOCHES', cols[3], stayY);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...dark);
  doc.setFontSize(9);

  doc.text(invoice.propertyNickname || '', cols[0], stayY + 6);
  doc.text(formatDateES(invoice.checkIn), cols[1], stayY + 6);
  doc.text(formatDateES(invoice.checkOut), cols[2], stayY + 6);
  doc.text(String(invoice.nights || ''), cols[3], stayY + 6);

  // Con datos de facturación propios el cliente ya no es quien se alojó, así
  // que el huésped consta aquí: la estancia sigue siendo suya
  const stayNotes = [
    invoice.channel ? `Canal: ${invoice.channel}` : '',
    cliente.isCustom && invoice.guestName ? `Huésped: ${invoice.guestName}` : '',
  ].filter(Boolean).join('   ·   ');

  if (stayNotes) {
    doc.setFontSize(7.5);
    doc.setTextColor(...gray);
    doc.text(stayNotes, cols[0], stayY + 12);
  }

  y += 28;

  // ─── Line items table ─────────────────────────────────────────────
  doc.setFillColor(...accent);
  doc.roundedRect(M, y, W - M * 2, 8, 2, 2, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text('CONCEPTO', M + 4, y + 5);
  doc.text('BASE', 118, y + 5, { align: 'right' });
  doc.text('IVA 10%', 148, y + 5, { align: 'right' });
  doc.text('TOTAL', W - M - 2, y + 5, { align: 'right' });
  y += 11;

  doc.setTextColor(...dark);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);

  if (invoice.invoiceModel === 'explotacion') {
    // Explotación: single line — cleaning already included in total price
    doc.text('Alquiler alojamiento turístico', M + 4, y);
    doc.text(fmt(invoice.baseAmount), 118, y, { align: 'right' });
    doc.text(fmt(invoice.ivaAmount), 148, y, { align: 'right' });
    doc.text(fmt(invoice.totalAmount), W - M - 2, y, { align: 'right' });
    y += 6;
  } else {
    // Gestión: show accommodation and cleaning separately
    if (invoice.accommodationTotal > 0) {
      doc.text('Alojamiento turístico', M + 4, y);
      doc.text(fmt(invoice.accommodationBase), 118, y, { align: 'right' });
      doc.text(fmt(invoice.accommodationIva), 148, y, { align: 'right' });
      doc.text(fmt(invoice.accommodationTotal), W - M - 2, y, { align: 'right' });
      y += 6;
    }
    if (invoice.cleaningFee > 0) {
      doc.text('Limpieza', M + 4, y);
      doc.text(fmt(invoice.cleaningBase), 118, y, { align: 'right' });
      doc.text(fmt(invoice.cleaningIva), 148, y, { align: 'right' });
      doc.text(fmt(invoice.cleaningFee), W - M - 2, y, { align: 'right' });
      y += 6;
    }
  }

  // Separator
  y += 2;
  doc.setDrawColor(...border);
  doc.setLineWidth(0.3);
  doc.line(M, y, W - M, y);
  y += 4;

  // Totals
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...gray);

  // Base imponible
  doc.text('Base imponible', M + 4, y);
  doc.setTextColor(...dark);
  doc.text(fmt(invoice.baseAmount), W - M - 2, y, { align: 'right' });
  y += 5.5;

  // IVA
  doc.setTextColor(...gray);
  doc.text('IVA (10%)', M + 4, y);
  doc.setTextColor(...dark);
  doc.text(fmt(invoice.ivaAmount), W - M - 2, y, { align: 'right' });
  y += 8;

  // Total
  doc.setDrawColor(...accent);
  doc.setLineWidth(0.8);
  doc.line(110, y - 2, W - M, y - 2);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...dark);
  doc.text('TOTAL', M + 4, y + 3);
  doc.text(fmt(invoice.totalAmount), W - M - 2, y + 3, { align: 'right' });
  y += 14;

  // ─── Footer ────────────────────────────────────────────────────────
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...gray);
  doc.text('IVA aplicado al 10% conforme a la Ley 37/1992 del IVA, art. 91. Alojamiento turístico.', M, 275);

  // Save
  const filename = `Factura_${invoice.invoiceNumber.replace(/\//g, '-')}_${(cliente.name || 'Cliente').replace(/\s+/g, '_')}.pdf`;
  doc.save(filename);
}
